import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, ArrowLeft, Send } from "lucide-react";

/**
 * Public contact page. Same form as the one in the footer modal — kept as a
 * standalone route so we can link to it from legal pages (privacy policy)
 * without exposing anyone's personal email.
 */
export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Message sent. We'll reply by email as soon as we can.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:py-14">
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-xl"
                style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}
            >
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-white/20 blur-2xl" aria-hidden />
                <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
                <div className="relative flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/95 text-primary flex items-center justify-center shadow-lg shrink-0">
                        <Mail className="w-7 h-7" strokeWidth={2.2} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">Get in touch</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight">Contact us</h1>
                        <p className="text-white/90 mt-1 text-sm sm:text-base">
                            Questions, feedback, privacy requests, or a problem to report — we'll reply as soon as we can.
                        </p>
                    </div>
                </div>
            </motion.section>

            <div className="mt-6">
                <Link
                    to="/"
                    data-testid="contact-back-link"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>
            </div>

            <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="mt-6 bg-white border border-border/60 rounded-3xl p-5 sm:p-6"
            >
                <form onSubmit={submit} className="space-y-4" data-testid="contact-page-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="cp-name">Your name</Label>
                            <Input
                                id="cp-name" required data-testid="contact-page-name-input"
                                className="mt-1.5 rounded-xl h-11"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label htmlFor="cp-email">Your email</Label>
                            <Input
                                id="cp-email" type="email" required data-testid="contact-page-email-input"
                                className="mt-1.5 rounded-xl h-11"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="cp-subject">Subject (optional)</Label>
                        <Input
                            id="cp-subject" data-testid="contact-page-subject-input"
                            className="mt-1.5 rounded-xl h-11"
                            placeholder="e.g. Privacy request, feedback, bug report…"
                            value={form.subject}
                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label htmlFor="cp-message">Message</Label>
                        <Textarea
                            id="cp-message" required data-testid="contact-page-message-input"
                            rows={6}
                            className="mt-1.5 rounded-xl"
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                        />
                    </div>
                    <Button
                        type="submit" disabled={submitting}
                        data-testid="contact-page-submit-btn"
                        className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 font-semibold"
                    >
                        <Send className="w-4 h-4 mr-1" />
                        {submitting ? "Sending…" : "Send message"}
                    </Button>
                </form>
            </motion.section>
        </div>
    );
}
